/**
 * Every permission the server enforces (FR-RBAC-02). The UI reads the same list to hide
 * controls, but the API is the only place a permission decision is trusted.
 */
export const PERMISSIONS = [
  "tenant.settings.manage",
  "tenant.billing.manage",
  "tenant.export",
  "branch.manage",
  "member.manage",
  "role.manage",
  "audit.view",

  "item.view",
  "item.manage",
  "item.cost.view",
  "price.override",

  "stock.view",
  "stock.adjust",
  "stock.adjust.approve",
  "stock.count",
  "stock.transfer",

  "sale.create",
  "sale.discount",
  "sale.discount.above_limit",
  "sale.void",
  "sale.return",
  "register.manage",
  "register.close_any",

  "purchase.view",
  "purchase.manage",
  "supplier.manage",

  "customer.view",
  "customer.manage",
  "customer.credit.override",
  "due.collect",

  "payment.manage",
  "expense.manage",
  "cashbook.view",

  "report.sales.view",
  "report.profit.view",
  "report.stock.view",
  "report.finance.view",
  "vat.manage",

  "sms.send",
  "storefront.manage",
  "order.manage",
  "developer.manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}

export const BUILT_IN_ROLES = [
  "owner",
  "manager",
  "cashier",
  "stock_keeper",
  "accountant",
] as const;
export type BuiltInRole = (typeof BUILT_IN_ROLES)[number];

const MANAGER: readonly Permission[] = PERMISSIONS.filter(
  (p) =>
    !p.startsWith("tenant.") &&
    p !== "role.manage" &&
    p !== "developer.manage" &&
    p !== "member.manage",
);

export const BUILT_IN_ROLE_PERMISSIONS: Record<BuiltInRole, readonly Permission[]> = {
  owner: PERMISSIONS,
  manager: MANAGER,
  cashier: [
    "item.view",
    "stock.view",
    "sale.create",
    "sale.discount",
    "sale.return",
    "customer.view",
    "customer.manage",
    "due.collect",
    "register.manage",
  ],
  stock_keeper: [
    "item.view",
    "item.manage",
    "stock.view",
    "stock.adjust",
    "stock.count",
    "stock.transfer",
    "purchase.view",
    "purchase.manage",
    "supplier.manage",
    "report.stock.view",
  ],
  accountant: [
    "item.view",
    "item.cost.view",
    "stock.view",
    "purchase.view",
    "customer.view",
    "payment.manage",
    "expense.manage",
    "cashbook.view",
    "report.sales.view",
    "report.profit.view",
    "report.stock.view",
    "report.finance.view",
    "vat.manage",
    "tenant.export",
    "audit.view",
  ],
};

export const BUILT_IN_ROLE_NAMES: Record<BuiltInRole, { en: string; bn: string }> = {
  owner: { en: "Owner", bn: "মালিক" },
  manager: { en: "Manager", bn: "ম্যানেজার" },
  cashier: { en: "Cashier", bn: "ক্যাশিয়ার" },
  stock_keeper: { en: "Stock keeper", bn: "স্টক কিপার" },
  accountant: { en: "Accountant", bn: "হিসাবরক্ষক" },
};

/**
 * How far Zeetabyte staff may go on a support visit: look, change everyday data, or act as the
 * owner (admins only).
 */
export const SUPPORT_MODES = ["read", "write", "full"] as const;
export type SupportMode = (typeof SUPPORT_MODES)[number];
